import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { onTechnicianCard, type LicenceStandingKey } from '@fieldops/shared'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CommitInput,
  dateFromInput,
  readCard,
  UndoNotice,
  useCardWriter,
  useLicenceWrites,
  useTicketWriter,
  useUndo,
  useWriteThrough,
  type LicenceWrites,
} from '../../components/licences/kit.js'
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  FilterChip,
  SkeletonRows,
  SwitchTrack,
} from '../../components/ui.js'
import { cn } from '../../lib/cn.js'
import { formatDate } from '../../lib/format.js'
import {
  attentionLabel,
  dateInputValue,
  daysLeftLabel,
  licenceShortName,
  needsAttention,
  standingBadge,
  standingDetail,
  STANDING_FILTERS,
} from '../../lib/licences.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'

/**
 * Every ticket in the business, as a grid: a row per person, a column per
 * ticket, and a square whose colour is its standing.
 *
 * The five chips are the five keys `licenceStanding` returns and their counts
 * are the server's, so this screen cannot disagree with the blocking banner on
 * a job card. A chip dims every square that is not in that state and hides the
 * people with none. Pressing a square opens its editor under the row, so a
 * renewal can be typed in without leaving the grid.
 *
 * The business Pest Controller's Licence is a strip rather than a row, because
 * it is not a person's ticket: every technician works under it, so if it lapses
 * they are all unlicensed whatever their own cards say. See
 * `_agent/sa-pest-licensing.md`.
 */

type Overview = RouterOutputs['licences']['overview']
type LicenceType = Overview['types'][number]
type Person = Overview['people'][number]
type Holding = NonNullable<Overview['rows'][number]['holding']>

type Cell = {
  licenceType: LicenceType
  holding: Holding | null
  standing: LicenceStandingKey
  daysLeft: number | null
  isGap: boolean
}

type Row = { person: Person; cells: Cell[]; attention: number }

const SQUARE: Record<LicenceStandingKey, string> = {
  held: 'bg-emerald-500',
  due: 'bg-amber-500',
  undated: 'bg-white ring-[3px] ring-inset ring-amber-500',
  expired: 'bg-red-500',
  missing: 'bg-ink-100 ring-1 ring-inset ring-ink-300',
}

const LEGEND: ReadonlyArray<{ key: LicenceStandingKey; label: string }> = [
  { key: 'held', label: 'Current' },
  { key: 'due', label: 'Due soon' },
  { key: 'undated', label: 'No date' },
  { key: 'expired', label: 'Expired' },
  { key: 'missing', label: 'Not held' },
]

const DOT: Record<LicenceStandingKey, string> = {
  held: 'bg-emerald-500',
  due: 'bg-amber-500',
  undated: 'bg-amber-500',
  expired: 'bg-red-500',
  missing: 'bg-red-500',
}

function buildRows(overview: Overview): Row[] {
  const byKey = new Map(overview.rows.map((row) => [row.key, row]))

  return overview.people.map((person) => {
    const cells = overview.types.map((licenceType): Cell => {
      const row = byKey.get(`${person.id}:${licenceType.id}`)
      return {
        licenceType,
        holding: row?.holding ?? null,
        standing: row?.standing ?? 'missing',
        daysLeft: row?.daysLeft ?? null,
        // The server writes a `missing` row only for an expected ticket on
        // somebody who goes out, which is exactly a gap.
        isGap: row !== undefined && row.holding === null,
      }
    })

    return {
      person,
      cells,
      attention: cells.filter((cell) => needsAttention(cell.standing, cell.isGap)).length,
    }
  })
}

/** Whether a square is what the chip asked for. Missing means a gap, as its count does. */
function matches(cell: Cell, filter: LicenceStandingKey): boolean {
  return filter === 'missing' ? cell.isGap : cell.standing === filter
}

export function CertificationsView() {
  const trpc = useTRPC()
  const overview = useQuery(trpc.licences.overview.queryOptions())
  const writes = useLicenceWrites()
  const [undo, offerUndo, dismissUndo] = useUndo()

  const [filter, setFilter] = useState<LicenceStandingKey | null>(null)
  const [open, setOpen] = useState<{ userId: string; typeId: string } | null>(null)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const rows = useMemo(() => (overview.data ? buildRows(overview.data) : []), [overview.data])

  if (overview.error) return <ErrorState message={errorMessage(overview.error)} />
  if (overview.isPending) return <SkeletonRows rows={8} />

  const { types, counts } = overview.data
  const shown = filter ? rows.filter((row) => row.cells.some((cell) => matches(cell, filter))) : rows
  const columns = `220px repeat(${types.length}, 36px) minmax(0, 1fr)`

  return (
    <>
      <BusinessLicenceStrip business={overview.data.business} />

      <div className="flex flex-wrap items-center gap-1.5 border-b border-ink-200 px-[var(--row-pad-x)] py-2.5">
        {STANDING_FILTERS.map((chip) => {
          const active = filter === chip.key
          return (
            <FilterChip
              key={chip.key}
              active={active}
              onClick={() => setFilter(active ? null : chip.key)}
              className="gap-1.5 px-2.5"
            >
              <span
                aria-hidden
                className={cn('size-1.5 rounded-full', active ? 'bg-white' : DOT[chip.key])}
              />
              {chip.label}
              <span className={cn('tabular-nums', active ? 'text-ink-300' : 'text-ink-400')}>
                {counts[chip.key]}
              </span>
            </FilterChip>
          )
        })}
        <span className="ml-auto hidden text-[11px] text-ink-500 sm:inline">
          Click a square to edit it
        </span>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="Nobody here yet" description="Add a person, then record their tickets." />
      ) : shown.length === 0 ? (
        <EmptyState
          title="Nothing in that state"
          description="Press the chip again to see everyone."
        />
      ) : (
        <div className="overflow-x-auto">
          <div style={{ minWidth: 220 + types.length * 36 + 48 }}>
            <div
              className="grid items-end border-b border-ink-200 px-[var(--row-pad-x)] pt-2 pb-1.5"
              style={{ gridTemplateColumns: columns }}
            >
              <span className="sticky left-0 z-10 self-stretch bg-white pt-28 text-[11px] font-medium tracking-wide text-ink-400 uppercase">
                Person
              </span>
              {types.map((type) => (
                <span
                  key={type.id}
                  title={type.name}
                  className={cn(
                    'h-28 rotate-180 justify-self-center text-[11px] whitespace-nowrap [writing-mode:vertical-rl]',
                    open?.typeId === type.id ? 'font-semibold text-ink-900' : 'text-ink-500',
                  )}
                >
                  {licenceShortName(type)}
                </span>
              ))}
              <span />
            </div>

            {shown.map((row) => {
              const openCell =
                open?.userId === row.person.id
                  ? row.cells.find((cell) => cell.licenceType.id === open.typeId)
                  : undefined

              return (
                <div key={row.person.id} className="border-b border-ink-100">
                  <div
                    className="grid items-center px-[var(--row-pad-x)] py-2"
                    style={{ gridTemplateColumns: columns }}
                  >
                    <Link
                      to={`/people/${row.person.id}?tab=certifications`}
                      className="sticky left-0 z-10 flex min-w-0 items-center gap-2 bg-white hover:opacity-80"
                    >
                      <Avatar name={row.person.name} colour={row.person.colour} />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ink-900">
                          {row.person.name}
                        </span>
                        <span
                          className={cn(
                            'block text-[11px]',
                            row.attention > 0 ? 'text-red-700' : 'text-ink-500',
                          )}
                        >
                          {attentionLabel(row.attention)}
                        </span>
                      </span>
                    </Link>

                    {row.cells.map((cell) => {
                      const selected = openCell === cell
                      const label = standingBadge(cell.standing).label
                      return (
                        <button
                          key={cell.licenceType.id}
                          type="button"
                          title={`${licenceShortName(cell.licenceType)}: ${label}`}
                          aria-label={`${row.person.name}, ${cell.licenceType.name}: ${label}`}
                          aria-expanded={selected}
                          onClick={() =>
                            setOpen(
                              selected ? null : { userId: row.person.id, typeId: cell.licenceType.id },
                            )
                          }
                          className={cn(
                            'size-6 justify-self-center rounded-md transition-[opacity,transform] hover:scale-110',
                            SQUARE[cell.standing],
                            filter && !matches(cell, filter) && 'opacity-20',
                            selected && 'outline-2 outline-offset-2 outline-ink-900',
                          )}
                        />
                      )
                    })}
                    <span />
                  </div>

                  {openCell ? (
                    <CellEditor
                      key={`${row.person.id}:${openCell.licenceType.id}`}
                      row={row}
                      cell={openCell}
                      writes={writes}
                      onClose={() => setOpen(null)}
                      onCleared={offerUndo}
                    />
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3.5 px-[var(--row-pad-x)] py-2.5 text-[11px] text-ink-600">
        {LEGEND.map((entry) => (
          <span key={entry.key} className="flex items-center gap-1.5">
            <span aria-hidden className={cn('size-2.5 rounded-[3px]', SQUARE[entry.key])} />
            {entry.label}
          </span>
        ))}
        {writes.failure ? (
          <span className="ml-auto text-red-700">Not saved: {writes.failure}</span>
        ) : null}
      </div>

      <UndoNotice undo={undo} onDone={dismissUndo} />
    </>
  )
}

/* -------------------------------------------------------------------------- */

function CellEditor({
  row,
  cell,
  writes,
  onClose,
  onCleared,
}: {
  row: Row
  cell: Cell
  writes: LicenceWrites
  onClose: () => void
  onCleared: (label: string, undo: () => void) => void
}) {
  const badge = standingBadge(cell.standing)
  const { licenceType } = cell

  return (
    <div className="mx-[var(--row-pad-x)] mb-3 flex flex-col gap-3 rounded-lg bg-ink-50 p-3 ring-1 ring-inset ring-ink-200">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink-900">{licenceType.name}</p>
          <p className="mt-0.5 text-xs text-ink-500">{standingDetail(cell)}</p>
        </div>
        <Badge tone={badge.tone}>{badge.label}</Badge>
      </div>

      {onTechnicianCard(licenceType) ? (
        <CardCellEditor row={row} cell={cell} writes={writes} onClose={onClose} />
      ) : licenceType.expires ? (
        <TicketCellEditor
          row={row}
          cell={cell}
          writes={writes}
          onClose={onClose}
          onCleared={onCleared}
        />
      ) : (
        <div className="flex items-center justify-between gap-3">
          <SwitchRow
            checked={cell.holding !== null}
            label="Signed off. This one doesn’t expire."
            onClick={() =>
              cell.holding
                ? writes.remove.mutate({ id: cell.holding.id })
                : writes.record.mutate({
                    userId: row.person.id,
                    licenceTypeId: licenceType.id,
                    reference: null,
                  })
            }
          />
          <Button size="sm" variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      )}
    </div>
  )
}

function CardCellEditor({
  row,
  cell,
  writes,
  onClose,
}: {
  row: Row
  cell: Cell
  writes: LicenceWrites
  onClose: () => void
}) {
  const card = readCard(row.cells.filter((other) => onTechnicianCard(other.licenceType)))
  const write = useCardWriter(row.person.id, card, writes)
  const id = cell.licenceType.id

  const serverOn = card.heldIds.includes(id)
  const [shown, setShown] = useState<boolean | null>(null)
  useEffect(() => setShown(null), [serverOn])
  const on = shown ?? serverOn

  const first = row.person.name.split(' ')[0]

  return (
    <>
      <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_170px_150px_auto]">
        <SwitchRow
          checked={on}
          label="On their technician card"
          onClick={() => {
            setShown(!on)
            void write({
              heldIds: on ? card.heldIds.filter((other) => other !== id) : [...card.heldIds, id],
            })
          }}
        />
        <Field label="Card number" htmlFor={`card-${row.person.id}`}>
          <CommitInput
            id={`card-${row.person.id}`}
            value={card.reference}
            placeholder="Add number"
            className="min-h-8 font-mono text-xs"
            onCommit={(reference) => void write({ reference })}
          />
        </Field>
        <Field label="Card expires" htmlFor={`card-exp-${row.person.id}`}>
          <CommitInput
            id={`card-exp-${row.person.id}`}
            type="date"
            value={card.expiresAt}
            className="min-h-8 text-xs"
            onCommit={(expiresAt) => void write({ expiresAt })}
          />
        </Field>
        <Button size="sm" variant="primary" onClick={onClose}>
          Done
        </Button>
      </div>
      <p className="text-[11px] text-ink-500">
        The card number and expiry are shared by every endorsement on the card, so changing them
        here changes them for all of {first}&rsquo;s endorsements.
      </p>
    </>
  )
}

function TicketCellEditor({
  row,
  cell,
  writes,
  onClose,
  onCleared,
}: {
  row: Row
  cell: Cell
  writes: LicenceWrites
  onClose: () => void
  onCleared: (label: string, undo: () => void) => void
}) {
  const write = useTicketWriter(row.person.id, cell, writes)
  const { holding, licenceType } = cell
  const fieldId = `${row.person.id}-${licenceType.id}`

  const clear = () => {
    if (!holding) return
    const before = holding
    writes.remove.mutate(
      { id: before.id },
      {
        onSuccess: () =>
          onCleared(`Cleared ${licenceShortName(licenceType)} for ${row.person.name}`, () =>
            writes.record.mutate({
              userId: row.person.id,
              licenceTypeId: licenceType.id,
              reference: before.reference,
              issuedAt: before.issuedAt,
              expiresAt: before.expiresAt,
              attachmentId: before.attachmentId,
            }),
          ),
      },
    )
  }

  return (
    <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_170px_150px_auto]">
      <span className="hidden sm:block" />
      <Field label="Number" htmlFor={`ref-${fieldId}`}>
        <CommitInput
          id={`ref-${fieldId}`}
          value={holding?.reference ?? ''}
          placeholder="Optional"
          className="min-h-8 font-mono text-xs"
          onCommit={(reference) => void write({ reference })}
        />
      </Field>
      <Field label="Expires" htmlFor={`exp-${fieldId}`}>
        <CommitInput
          id={`exp-${fieldId}`}
          type="date"
          value={dateInputValue(holding?.expiresAt)}
          className="min-h-8 text-xs"
          onCommit={(expiresAt) => void write({ expiresAt })}
        />
      </Field>
      <div className="flex gap-1.5">
        {holding ? (
          <Button size="sm" variant="ghost" onClick={clear}>
            Clear
          </Button>
        ) : null}
        <Button size="sm" variant="primary" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  )
}

function SwitchRow({
  checked,
  label,
  onClick,
}: {
  checked: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onClick}
      className="flex min-h-8 items-center gap-2.5 text-left text-[13px] text-ink-700"
    >
      <SwitchTrack checked={checked} />
      {label}
    </button>
  )
}

/* -------------------------------------------------------------------------- */

/**
 * The business's own licence, edited where it is read.
 *
 * Two columns on `Setting` (decision 66), so the write is `settings.update` and
 * the pencil is `SETTINGS_MANAGE`. Without it the strip is read-only.
 */
function BusinessLicenceStrip({ business }: { business: Overview['business'] }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const [editing, setEditing] = useState(false)

  const update = useMutation(
    trpc.settings.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.licences.pathFilter())
        void queryClient.invalidateQueries(trpc.settings.pathFilter())
      },
    }),
  )

  const write = useWriteThrough(
    { number: business.number ?? '', expiresAt: dateInputValue(business.expiresAt) },
    (next) =>
      update.mutateAsync({
        pestControllerLicenceNumber: next.number || null,
        pestControllerLicenceExpiresAt: dateFromInput(next.expiresAt),
      }),
  )

  const badge = standingBadge(business.standing)
  const missing = business.number === null
  const canEdit = can('SETTINGS_MANAGE')

  return (
    <div
      className={cn(
        'border-b px-[var(--row-pad-x)] py-2.5',
        business.standing === 'held' ? 'border-ink-200 bg-white' : 'border-amber-200 bg-amber-50',
      )}
    >
      {editing ? (
        <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_180px_160px_auto]">
          <div>
            <p className="text-sm font-semibold text-ink-900">Pest Controller&rsquo;s Licence</p>
            <p className="mt-0.5 text-xs text-ink-600">
              Every technician works under the business licence, so this is the one that matters
              most.
            </p>
          </div>
          <Field label="Licence number" htmlFor="business-licence">
            <CommitInput
              id="business-licence"
              value={business.number ?? ''}
              placeholder="Add number"
              className="min-h-8 font-mono text-[13px]"
              onCommit={(number) => void write({ number })}
            />
          </Field>
          <Field label="Expires" htmlFor="business-licence-expires">
            <CommitInput
              id="business-licence-expires"
              type="date"
              value={dateInputValue(business.expiresAt)}
              className="min-h-8 text-xs"
              onCommit={(expiresAt) => void write({ expiresAt })}
            />
          </Field>
          <Button size="sm" variant="primary" onClick={() => setEditing(false)}>
            Done
          </Button>
          {update.error ? (
            <p className="text-xs text-red-700 sm:col-span-4">{errorMessage(update.error)}</p>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-ink-900">
              Pest Controller&rsquo;s Licence
              <Badge tone={badge.tone}>{badge.label}</Badge>
            </p>
            <p className="mt-0.5 text-xs text-ink-600">
              {missing
                ? 'Not recorded. Every technician works under the business licence, so this is the one that matters most.'
                : `${business.number}${
                    business.expiresAt
                      ? ` · expires ${formatDate(business.expiresAt)} · ${daysLeftLabel(business.daysLeft).toLowerCase()}`
                      : ' · no expiry recorded'
                  }`}
            </p>
          </div>

          {canEdit ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-brand-700 hover:bg-amber-100"
              onClick={() => setEditing(true)}
            >
              {missing ? 'Add it' : 'Edit'}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  )
}

